<template>
  <div class="setting-profile px-8">
    <DiscardChangesDialog
      :visible="isDiscardDialogOpen"
      @closeDialog="closeDialog"
      @closeDialogWithoutFormLeave="isDiscardDialogOpen = false"
    />
    <DeleteAccountDialog
      v-if="isDeleteAccountDialogOpen"
      :visible="isDeleteAccountDialogOpen"
      :userPhone="userDetailsForBookingDetails.phone"
      @closeDialog="isDeleteAccountDialogOpen = false"
      @closeDialogWithoutFormLeave="isDeleteAccountDialogOpen = false"
      @onDeleteAccountRequestedSuccess="
        isDeleteAccountDialogOpen = false;
        isSuccessDialogOpen = true;
      "
    />
    <VDialog
      :value="isSuccessDialogOpen"
      width="425"
      @click:outside="isSuccessDialogOpen = false"
    >
      <SuccessDialog
        :caption="$t('settings.deleteAccount.title')"
        :body="$t('settings.deleteAccount.successMessage')"
        @closeDialog="isSuccessDialogOpen = false"
      />
    </VDialog>
    <VCard>
      <ViewProfile
        v-if="!isEditMode && !isProfileLoading"
        :profile="userDetailsForBookingDetails"
        :firstLetters="firstLetters"
        @openForm="toEditMode"
        @openDeleteAccountDialog="isDeleteAccountDialogOpen = true"
      />
      <ViewProfileSkeletonLoader v-else-if="isProfileLoading" />
      <EditProfile
        v-else
        ref="edit"
        :profile="userDetailsForBookingDetails"
        @closeForm="closeForm"
        @updateProfile="update"
      />
    </VCard>
  </div>
</template>

<script lang="ts">
import type Vue from 'vue';

export default {
  name: 'SSettingsProfile',
  beforeRouteLeave(this: Vue, to: unknown, from: unknown, next: (arg?: unknown) => void) {
    const edit = this.$refs.edit as { cancel?: () => void } | undefined;
    if (edit) {
      edit.cancel?.();
      if ((this as Vue & { avoidRouteLeave?: boolean }).avoidRouteLeave) {
        next();
      } else {
        next(false);
      }
    } else {
      next();
    }
  },
};
</script>

<script setup lang="ts">
import { ref } from 'vue';
import SUserService from '@slg/web-customer-shared/src/services/UserService';
import { useGlobalErrorDialog } from '../../../composables';
import DiscardChangesDialog from './DiscardChangesDialog.vue';
import DeleteAccountDialog from './DeleteAccountDialog.vue';
import SuccessDialog from '../SuccessDialog.vue';
import EditProfile from './EditProfile.vue';
import ViewProfile from './ViewProfile.vue';
import ViewProfileSkeletonLoader from './ViewProfileSkeletonLoader.vue';

const props = defineProps({
  firstLetters: {
    type: String,
    default: '',
  },
  userDetailsForBookingDetails: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits(['populateUserProfileInfo', 'setGlobalLoading']);

const { setGlobalError } = useGlobalErrorDialog();

const currentDialog = ref('');
const isDiscardDialogOpen = ref(false);
const isDeleteAccountDialogOpen = ref(false);
const isSuccessDialogOpen = ref(false);
const isEditMode = ref(false);
const avoidRouteLeave = ref(true);
const isProfileLoading = ref(false);

async function populateProfile() {
  isProfileLoading.value = true;
  await emit('populateUserProfileInfo');
  isProfileLoading.value = false;
}

populateProfile();

function toEditMode() {
  isEditMode.value = true;
}

async function update(newProfile: any) {
  const profile = {
    ...newProfile,
    firstName: newProfile.firstName.trim(),
    lastName: newProfile.lastName.trim(),
  };
  emit('setGlobalLoading', true);
  try {
    await SUserService.updateProfile(profile);
    await emit('populateUserProfileInfo');
    isEditMode.value = false;
  } catch (e) {
    if ((e as any).status !== 401) {
      await setGlobalError(e);
    }
  }
  emit('setGlobalLoading', false);
}

function closeForm(newProfile: any) {
  if (
    Object.entries(props.userDetailsForBookingDetails).toString()
    === Object.entries(newProfile).toString()
  ) {
    isEditMode.value = false;
  } else {
    avoidRouteLeave.value = false;
    isDiscardDialogOpen.value = true;
  }
}

function closeDialog() {
  isDiscardDialogOpen.value = false;
  isEditMode.value = false;
  avoidRouteLeave.value = false;
}
</script>
<style lang="scss" scoped>
.setting-profile {
  max-width: 810px;
  width: 100%;

  @media screen and (min-width: 600px) {
    width: 70%;
  }

  @media screen and (min-width: 960px) {
    width: 50%;
  }
}
</style>
